import { ChartInsight } from '@api';
import { startOfDayUTC } from '@chart';
import { Domain } from '@chart/types/axis.types';
import { XAxis, YAxis } from '@chart/types/create.types';
import {
  ChartAssetData,
  GroupedInsights,
  InsightsInDomain,
  TimeSeriesItem,
} from '@chart/types/timeseries.types';
import { bisector } from 'd3-array';
import { isSameDay } from 'date-fns';

export type InsightsByDate = Record<string, GroupedInsights>;

export const groupSingleInsight = (
  insight: ChartInsight,
  insightsByDate: InsightsByDate
) => {
  // Insight date comes from `created_at` which are created at midnight 00:00 in UTC
  const date = insight.created_at.split('T')[0];

  const group = insightsByDate[date];

  if (!group) {
    insightsByDate[date] = {
      date: new Date(date),
      insights: [insight],
    };
  } else {
    group.insights.push(insight);
  }

  return insightsByDate;
};

export const groupInsightsByDate = (insights: ChartInsight[]) =>
  insights.reduce((insightsByDate: InsightsByDate, insight: ChartInsight) => {
    groupSingleInsight(insight, insightsByDate);
    return insightsByDate;
  }, {});

export interface InsightInDomainProps {
  primaryAsset: ChartAssetData;
  domain: Domain;
  insights?: ChartInsight[];
  y: YAxis[];
  x: XAxis;
  width: number;
}

export const formatDate = (time: string) => {
  return startOfDayUTC(new Date(time));
};

// insights can be created on a date which doesn't exist in ts(data gap)
// or insight can be created let's say at 2024-09-09 3AM but we only have 2024-09-09 8AM in ts data --> insight will be display at 8AM
// so, it means we treat all insights as created at the very beginning of the day
// so that when we search for the next nearest ts point, it's guaranteed to be the same day
// if any exist on the same day as the insight
export const getInsightsInDomain = ({
  primaryAsset,
  domain,
  insights,
  y,
  x,
  width,
}: InsightInDomainProps) => {
  const insightsInDomain: InsightsInDomain[] = [];

  if (!insights?.length) {
    return insightsInDomain;
  }

  const data = primaryAsset.ts;
  const bis = bisector((d: TimeSeriesItem) => formatDate(d.time).getTime());
  const roundedDomain = [Math.ceil(domain[0]), Math.floor(domain[1])];
  const top = y[0].yScale.range()[0];

  const groupedInsightsByDate: InsightsByDate = groupInsightsByDate(insights);
  const usedIndexes = new Map<number, number>();

  Object.entries(groupedInsightsByDate).map(([dateKey, groupedInsights]) => {
    const insightDate = formatDate(dateKey);
    const dateTime = insightDate.getTime();
    const index = bis.left(data, dateTime, roundedDomain[0], roundedDomain[1]);
    const foundDate = formatDate(data[index].time);

    const isOnDomainBoundary =
      index === roundedDomain[0] || index === roundedDomain[1];
    const sameDayMatch = isSameDay(insightDate, foundDate);

    // exclude insights outside of the domain
    if (!isOnDomainBoundary || sameDayMatch) {
      const yOffsetCount = usedIndexes.get(index) ?? -1;

      if (!sameDayMatch) {
        usedIndexes.set(index, yOffsetCount + 1);
      }

      insightsInDomain.push({
        top,
        right: width - x.xScale(index),
        groupedInsights,
        yOffsetCount: usedIndexes.get(index) ?? 0,
      });
    }
  });

  //this is required for simplifying arrow navigation
  //closest to the left edge will have 0 index, closest to the right edge - last index
  return insightsInDomain.sort((a, b) => b.right - a.right);
};
